import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, FormCard, SectionHeader } from "@fawkes/ui";

import { DagPensionVolList } from "./DagPensionVolList";
import { PensionCapaciteit } from "./PensionCapaciteit";
import { AccommodatieBeheer } from "../pensionplanning/AccommodatieBeheer";
import { PensionPrijsberekening } from "./PensionPrijsberekening";
import { PensionWizardIndeling } from "./PensionWizardIndeling";
import { PensionBrengHaaltijden } from "./PensionBrengHaaltijden";
import { PensionBenaming } from "./PensionBenaming";
import type { SettingsContext } from "./tiles/types";

import "./PensionSettings.css";

// settings-pension: the React port of the Jmix Instellingen → Pension tile (the branch-context
// "Algemeen" block of SettingsDashboardView.tilesForContext()). It groups the three sub-items the
// Jmix tile lists — Berekening pension (PensionEigenschap), Hoogseizoen dagen (DagPensionVol) and
// Capaciteit (config) — and opens each as its own list/form screen. Branch-scoped: the vestiging the
// caller selected in the settings context selector is threaded into the branch-scoped sub-screens.
//
// MODULE GATING (deliberately not faked): the Jmix view gates this tile on tileModule("pension") via
// ModuleAvailability. There is no equivalent module-availability signal in api/ yet, so the tile is
// shown whenever a branch context is active. Surfacing a real backend module signal is a documented
// follow-up — see the MR notes.

type PensionItem =
  | "pensionVol" | "capaciteit" | "accommodaties" | "prijsberekening" | "wizardIndeling" | "brengHaaltijden" | "benaming";

interface PensionSettingsProps {
  context: SettingsContext;
  onBack: () => void;
}

export function PensionSettings({ context, onBack }: PensionSettingsProps) {
  const t = useTranslate();
  const [item, setItem] = useState<PensionItem | null>(null);

  // Branch-scoped tile: the dashboard only opens it in a branch context, so vestigingId is present.
  const vestigingId = context.vestigingId ?? 0;
  const breadcrumb = `${t("instellingen.title")} › ${context.label} › ${t("instellingen.tile.pension.title")}`;

  if (item === "pensionVol") {
    return <DagPensionVolList vestigingId={vestigingId} onBack={() => setItem(null)} />;
  }
  if (item === "capaciteit") {
    return <PensionCapaciteit vestigingId={vestigingId} onBack={() => setItem(null)} />;
  }
  if (item === "prijsberekening") {
    return <PensionPrijsberekening vestigingId={vestigingId} onBack={() => setItem(null)} />;
  }
  if (item === "brengHaaltijden") {
    return <PensionBrengHaaltijden vestigingId={vestigingId} onBack={() => setItem(null)} />;
  }
  if (item === "benaming") {
    return <PensionBenaming onBack={() => setItem(null)} />;
  }
  if (item === "wizardIndeling") {
    return <PensionWizardIndeling vestigingId={vestigingId} onBack={() => setItem(null)} />;
  }
  if (item === "accommodaties") {
    return (
      <FormCard>
        <SectionHeader>{t("instellingen.item.accommodaties")}</SectionHeader>
        <AccommodatieBeheer vestigingId={vestigingId} />
        <div className="fawkes-list-toolbar">
          <Button variant="secondary" onClick={() => setItem(null)}>
            {t("common.back")}
          </Button>
        </div>
      </FormCard>
    );
  }

  const items: { key: PensionItem; labelKey: string; descKey: string }[] = [
    {
      key: "pensionVol",
      labelKey: "instellingen.item.pensionVol",
      descKey: "instellingen.pension.pensionVol.desc",
    },
    {
      key: "capaciteit",
      labelKey: "instellingen.item.capaciteit",
      descKey: "instellingen.pension.capaciteit.desc",
    },
    {
      key: "prijsberekening",
      labelKey: "instellingen.item.prijsberekening",
      descKey: "instellingen.pension.prijsberekening.desc",
    },
    {
      key: "brengHaaltijden",
      labelKey: "instellingen.item.brengHaaltijden",
      descKey: "instellingen.pension.brengHaaltijden.desc",
    },
    {
      key: "wizardIndeling",
      labelKey: "instellingen.item.wizardIndeling",
      descKey: "instellingen.pension.wizardIndeling.desc",
    },
    {
      key: "accommodaties",
      labelKey: "instellingen.item.accommodaties",
      descKey: "instellingen.pension.accommodaties.desc",
    },
    {
      key: "benaming",
      labelKey: "instellingen.item.benaming",
      descKey: "instellingen.pension.benaming.desc",
    },
  ];

  return (
    <FormCard>
      <p className="settings-tile-modal-breadcrumb">{breadcrumb}</p>
      <SectionHeader>{t("instellingen.tile.pension.title")}</SectionHeader>
      <ul className="pension-overview-list">
        {items.map((entry) => (
          <li key={entry.key} className="pension-overview-item">
            <button
              type="button"
              className="pension-overview-button"
              onClick={() => setItem(entry.key)}
            >
              <span className="pension-overview-label">{t(entry.labelKey)}</span>
              <span className="pension-overview-desc">{t(entry.descKey)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="fawkes-list-toolbar">
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </div>
    </FormCard>
  );
}
